import React from "react";
import { useTranslation } from "react-i18next";
import { FileDown } from "lucide-react";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExportTargetDescriptor, ExportTargetType } from "@/types/data-export";

interface ExportConfirmBodyProps {
  descriptor: ExportTargetDescriptor;
  isPending: boolean;
  serverError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ExportConfirmBody: React.FC<ExportConfirmBodyProps> = ({
  descriptor,
  isPending,
  serverError,
  onConfirm,
  onCancel,
}) => {
  const { t } = useTranslation();
  const lead =
    descriptor.type === ExportTargetType.GUEST
      ? t("dataExport.confirm.guestLead")
      : t("dataExport.confirm.userLead");

  return (
    <div className="flex flex-col gap-5">
      <DialogHeader>
        <div className="flex items-center gap-2 text-foreground">
          <FileDown className="size-5 text-royal-glow-3" />
          <DialogTitle>
            {t("dataExport.confirm.title", { label: descriptor.label })}
          </DialogTitle>
        </div>
      </DialogHeader>

      <p className="text-sm text-foreground">{lead}</p>

      {serverError ? (
        <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
          {serverError}
        </div>
      ) : null}

      <DialogFooter>
        <Button
          type="button"
          variant="new_outline"
          onClick={onCancel}
          disabled={isPending}
        >
          {t("dataExport.confirm.cancel")}
        </Button>
        <Button
          type="button"
          variant="new_default"
          onClick={onConfirm}
          disabled={isPending}
        >
          {isPending
            ? t("dataExport.confirm.submitting")
            : t("dataExport.confirm.submit")}
        </Button>
      </DialogFooter>
    </div>
  );
};
